import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Card, CardBody, Button, Row, Col, Table, Badge, Modal, Form, Alert, Container } from 'react-bootstrap';
import Breadcrumbs from '../../../Common/Breadcrumbs';
import AddNewButton from '../../../Common/AddNewButton';
import { DeleteModal } from '../../../Common/DeleteModal';
import Select from 'react-select';
import imageCompression from 'browser-image-compression';
import secureApi from '../../../utils/secureApi';
import './TeamInformation.css';
import '../../../components/PlayerComponents.css';
import { getImageUrl } from '../../../utils/imageUrl';
import PlayerAssetPreviewModal, { PlayerAssetType } from '../../../components/PlayerAssetPreviewModal';
import { PLAYER_ASSETS_ENABLED } from '../../../constants/featureFlags';
import { downloadFileFromUrl } from '../../../utils/fileDownload';



// Import player management components and services
import PlayerForm from '../../../components/PlayerForm';
import PlayerTable from '../../../Common/PlayerTable';

import { 
  getPlayersByTeamId, 
  getPlayerById,
  addPlayer, 
  updatePlayer, 
  deletePlayer,
  Player,
  AddPlayerRequest,
  UpdatePlayerRequest
} from '../../../api/club/playerService';
import { PlayerFormData } from '../../../types/Player';
import { getAgeCategoriesFromTournaments, getTeamById } from '../../../api/club/teamService';

interface Team {
  _id: string;
  name: string;
  shortName: string;
  logo: string;
  ageGroups: string[];
  coachManagerName: string;
  contactDetails: string;
  players: number;
  verifiedPlayers?: number;
  paidPlayers?: number;
  sports?: string;
  status?: string;
}

const TeamDetail: React.FC = () => {
  const { teamId } = useParams<{ teamId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const [team, setTeam] = useState<Team | null>(null);
  const [players, setPlayers] = useState<Player[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAddPlayerModal, setShowAddPlayerModal] = useState(false);
  const [showPlayerForm, setShowPlayerForm] = useState(false);
  const [editingPlayer, setEditingPlayer] = useState<Player | null>(null);
  const [isEditMode, setIsEditMode] = useState(true); // Start in edit mode by default
  const [ageCategories, setAgeCategories] = useState<string[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [hasFormChanges, setHasFormChanges] = useState(false);
  const [originalTeamData, setOriginalTeamData] = useState<any>(null);
  
  // Delete modal state
  const [showDeleteTeamModal, setShowDeleteTeamModal] = useState(false);
  const [showDeletePlayerModal, setShowDeletePlayerModal] = useState(false);
  const [deletePlayerTarget, setDeletePlayerTarget] = useState<Player | null>(null);
  const [deleteTeamError, setDeleteTeamError] = useState<string | null>(null);
  const [deletePlayerError, setDeletePlayerError] = useState<string | null>(null);
  const [isDeletingTeam, setIsDeletingTeam] = useState(false);
  const [isDeletingPlayer, setIsDeletingPlayer] = useState(false);
  const [assetPreviewConfig, setAssetPreviewConfig] = useState<{ player: any; type: PlayerAssetType } | null>(null);
  
  // Navigation state from location
  const navigationSource = location.state?.source;
  const isAddPlayerMode = navigationSource === 'add-player';
  const isManagePlayersMode = navigationSource === 'manage-players';
  const shouldCollapseTeamForm = location.state?.collapseTeamForm || false;
  
  // State for team form collapse/expand
  const [showTeamFormDetails, setShowTeamFormDetails] = useState(!shouldCollapseTeamForm);
  
  // Team form data
  const [teamFormData, setTeamFormData] = useState({
    name: '',
    shortName: '',
    logo: '',
    ageGroups: [] as string[],
    coachManagerName: '',
    contactDetails: '',
    sports: 'Football'
  });

  // File input refs
  const teamLogoRef = useRef<HTMLInputElement>(null);

  // Age category options
  const ageCategoryOptions = [
    { value: 'U7', label: 'U7' },
    { value: 'U10', label: 'U10' },
    { value: 'U11', label: 'U11' },
    { value: 'U12', label: 'U12' },
    { value: 'U13', label: 'U13' },
    { value: 'U14', label: 'U14' },
    { value: 'U15', label: 'U15' },
    { value: 'U16', label: 'U16' },
    { value: 'U17', label: 'U17' },
    { value: 'U18', label: 'U18' }
  ];

  // Mock data for team
  const mockTeam: Team = {
    _id: teamId || '1',
    name: 'Oranje Football Club',
    shortName: 'OFC',
    logo: '',
    ageGroups: ['U10', 'U12', 'U14'],
    coachManagerName: 'John Doe',
    contactDetails: '+91 9876543210',
    players: 15,
    verifiedPlayers: 12,
    paidPlayers: 10,
    sports: 'Football',
    status: 'active'
  };

  // Mock data for players
  const mockPlayers: Player[] = [
    {
      _id: '1',
      name: 'Player 1',
      profilePic: '',
      dob: '2015-05-15',
      ageCategory: 'U10',
      verificationStatus: 'approved',
      createdAt: '2024-01-15T10:30:00Z',
      documents: [
        {
          fileName: 'id_proof_1703123456789.pdf',
          key: 'my-club/id-proof/15-12-2023/id_proof_1703123456789.pdf',
          type: 'id-proof'
        },
        {
          fileName: 'consent_form_1703123456789.pdf',
          key: 'my-club/consent-form/15-12-2023/consent_form_1703123456789.pdf',
          type: 'consent-form'
        }
      ]
    },
    {
      _id: '2',
      name: 'Player 2',
      profilePic: '',
      dob: '2014-08-20',
      ageCategory: 'U10',
      verificationStatus: 'pending',
      createdAt: '2024-01-16T14:45:00Z',
      documents: []
    }
  ];

  useEffect(() => {
    loadTeamData();
    loadAgeCategories();
    
    // Handle navigation states
    if (isAddPlayerMode) {
      setShowPlayerForm(true);
      // Scroll to top when showing add player form
      setTimeout(() => {
        window.scrollTo({
          top: 0,
          behavior: 'smooth'
        });
      }, 100);
    }
  }, [teamId, isAddPlayerMode]);

  // Update team form data when team is loaded
  useEffect(() => {
    if (team) {
      const newFormData = {
        name: team.name,
        shortName: team.shortName,
        logo: team.logo || '',
        ageGroups: team.ageGroups,
        coachManagerName: team.coachManagerName,
        contactDetails: team.contactDetails,
        sports: team.sports || 'Football'
      };
      setTeamFormData(newFormData);
      setOriginalTeamData(newFormData);
      setHasFormChanges(false);
    }
  }, [team]);

  const loadAgeCategories = async () => {
    try {
      const categories = await getAgeCategoriesFromTournaments();
      setAgeCategories(categories);
    } catch (error) {
      console.error('Failed to load age categories:', error);
      // Set default categories if API fails
      setAgeCategories(['U7', 'U8', 'U9', 'U10', 'U11', 'U12', 'U13', 'U14', 'U15', 'U16', 'U17', 'U18']);
    }
  };

  const loadTeamData = async () => {
    try {
      setLoading(true);
      
      // Load team data from API
      if (teamId) {
        try {
          const teamDetails = await getTeamById(teamId);
          console.log('Team details fetched:', teamDetails);
          
          // Convert API team data to our Team interface
          const apiTeam: Team = {
            _id: teamDetails._id,
            name: teamDetails.name,
            shortName: teamDetails.shortName,
            logo: teamDetails.logo || '',
            ageGroups: Array.isArray(teamDetails.ageCategory) ? teamDetails.ageCategory : [teamDetails.ageCategory],
            coachManagerName: teamDetails.managerName || '',
            contactDetails: teamDetails.managerContactNum ? teamDetails.managerContactNum.toString() : '',
            players: 0, // Will be updated when players are loaded
            verifiedPlayers: 0,
            paidPlayers: 0,
            sports: 'Football',
            status: teamDetails.isActive ? 'active' : 'inactive'
          };
          
          console.log('Team logo URL:', apiTeam.logo);
          console.log('Full team data:', apiTeam);
          
          setTeam(apiTeam);
          
          // Load players from API
          try {
            const playersData = await getPlayersByTeamId(teamId);
            setPlayers(playersData);
            
            // Update team player count
            setTeam(prevTeam => prevTeam ? {
              ...prevTeam,
              players: playersData.length,
              verifiedPlayers: playersData.filter(p => p.verificationStatus === 'approved').length,
              paidPlayers: 0 // Payment status not available in current API
            } : null);
          } catch (error) {
            console.error('Failed to load players from API:', error);
            setPlayers([]);
          }
        } catch (error) {
          console.error('Failed to load team from API, using mock data:', error);
          setTeam(mockTeam);
          setPlayers(mockPlayers);
        }
      } else {
        setTeam(mockTeam);
        setPlayers(mockPlayers);
      }
    } catch (error) {
      console.error('Error loading team data:', error);
      setTeam(mockTeam);
      setPlayers(mockPlayers);
    } finally {
      setLoading(false);
    }
  };

  const handleAddPlayer = () => {
    setEditingPlayer(null); // Clear editing player to show empty form
    setShowAddPlayerModal(false);
    setShowPlayerForm(true);
    setSubmitError(null);
    
    // Scroll to top smoothly after form is shown
    setTimeout(() => {
      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    }, 100);
  };

  const handleEditPlayer = async (player: Player) => {
    try {
      // Fetch complete player data including documents
      const completePlayerData = await getPlayerById(player._id);
      console.log('Complete player data fetched:', completePlayerData);
      console.log('Player documents from API:', completePlayerData.documents);
      
      setEditingPlayer(completePlayerData);
      setShowAddPlayerModal(false);
      setShowPlayerForm(true);
      setSubmitError(null);
      
      // Scroll to top smoothly after form is shown
      setTimeout(() => {
        window.scrollTo({
          top: 0,
          behavior: 'smooth'
        });
      }, 100);
    } catch (error) {
      console.error('Failed to fetch complete player data:', error);
      // Fallback to basic player data if API fails
      setEditingPlayer(player);
      setShowAddPlayerModal(false);
      setShowPlayerForm(true);
      setSubmitError(null);
      
      setTimeout(() => {
        window.scrollTo({
          top: 0,
          behavior: 'smooth'
        });
      }, 100);
    }
  };

  const handleSavePlayer = async (playerData: PlayerFormData) => {
    try {
      setIsSubmitting(true);
      setSubmitError(null);

      if (!teamId) {
        throw new Error('Team ID not found');
      }

      if (editingPlayer) {
        // Update existing player
        const updateData: UpdatePlayerRequest = {
          name: playerData.name,
          profilePic: playerData.profilePic,
          dob: playerData.dob,
          ageCategory: playerData.ageCategory,
          parentName: playerData.parentName,
          documents: playerData.documents
        };

        const response = await updatePlayer(editingPlayer._id, updateData);
        
        if (response.success) {
          // Reload players to get updated data
          await loadTeamData();
          setShowPlayerForm(false);
          setEditingPlayer(null);
        }
      } else {
        // Add new player
        const addData: AddPlayerRequest = {
          name: playerData.name,
          profilePic: playerData.profilePic,
          dob: playerData.dob,
          ageCategory: playerData.ageCategory,
          parentName: playerData.parentName,
          documents: playerData.documents
        };

        const response = await addPlayer(teamId, addData);
        
        if (response.success) {
          // Reload players to get updated data
          await loadTeamData();
          setShowPlayerForm(false);
        }
      }
    } catch (error: any) {
      console.error('Failed to save player:', error);
      setSubmitError(error.message || 'Failed to save player. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancelAddPlayer = () => {
    setShowAddPlayerModal(false);
    setShowPlayerForm(false);
    setEditingPlayer(null);
    setSubmitError(null);
  };

  const handleDeletePlayer = async (playerId: string) => {
    const player = players.find(p => p._id === playerId);
    if (player) {
      setDeletePlayerTarget(player);
      setShowDeletePlayerModal(true);
    }
  };

const handleAssetView = (player: any, assetType: PlayerAssetType) => {
  setAssetPreviewConfig({ player, type: assetType });
};

const handleAssetDownload = (player: any, assetType: PlayerAssetType) => {
  const assetUrl = assetType === 'qrCode' ? player.qrCodeUrl : player.playerIdCardUrl;
  if (!assetUrl) return;

  const formattedName = player.playerId || player.fullName?.replace(/\s+/g, '-').toLowerCase();
  const filename =
    assetType === 'qrCode'
      ? `${formattedName || 'player'}-qr-code.png`
      : `${formattedName || 'player'}-id-card.pdf`;

  downloadFileFromUrl(assetUrl, filename);
};

  const confirmDeletePlayer = async () => {
    if (deletePlayerTarget) {
      try {
        setIsDeletingPlayer(true);
        setDeletePlayerError(null);
        
        console.log('Attempting to delete player:', deletePlayerTarget._id);
        await deletePlayer(deletePlayerTarget._id);
        console.log('Delete player successful');
        
        // Always close modal and refresh data if no exception was thrown
        // The API response shows success, so we should proceed
        
        // Close modal first
        setShowDeletePlayerModal(false);
        setDeletePlayerTarget(null);
        setDeletePlayerError(null);
        
        // Refresh team data to show updated player list
        await loadTeamData();
        
      } catch (error: any) {
        console.error('❌ Exception caught in confirmDeletePlayer:', error);
        console.error('Error response:', error.response?.data);
        console.error('Error status:', error.response?.status);
        
        // Show specific error message from API
        let errorMessage = 'Failed to delete player. Please try again.';
        
        if (error.response?.data) {
          const errorData = error.response.data;
          if (errorData.error) {
            errorMessage = errorData.error;
          } else if (errorData.message) {
            errorMessage = errorData.message;
          } else if (errorData.statusMessage) {
            errorMessage = errorData.statusMessage;
          }
        } else if (error.message) {
          errorMessage = error.message;
        }
        
        console.log('Setting error message:', errorMessage);
        setDeletePlayerError(errorMessage);
      } finally {
        console.log('=== Finishing delete player process ===');
        setIsDeletingPlayer(false);
      }
    }
  };

  const closeDeletePlayerModal = () => {
    setShowDeletePlayerModal(false);
    setDeletePlayerTarget(null);
    setDeletePlayerError(null);
  };

  const handleViewPlayer = async (player: Player) => {
    try {
      // Fetch complete player data including documents
      const completePlayerData = await getPlayerById(player._id);
      console.log('Complete player data for view:', completePlayerData);
      
      // Show player details in the same card format as Add Player
      setEditingPlayer(completePlayerData);
      setShowPlayerForm(true);
      setShowAddPlayerModal(false);
      
      // Scroll to top for better UX
      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    } catch (error) {
      console.error('Failed to fetch complete player data for view:', error);
      // Fallback to basic player data if API fails
      setEditingPlayer(player);
      setShowPlayerForm(true);
      setShowAddPlayerModal(false);
      
      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    }
  };

  const handleTeamFormChange = (field: string, value: any) => {
    setTeamFormData(prev => ({ ...prev, [field]: value }));
    
    // Check if form has changes
    const newFormData = { ...teamFormData, [field]: value };
    const hasChanges = JSON.stringify(newFormData) !== JSON.stringify(originalTeamData);
    setHasFormChanges(hasChanges);
  };

  const handleSaveTeam = async () => {
    try {
      setIsSubmitting(true);
      
      // Update team data
      console.log('Saving team:', teamFormData);
      
      // Here you would call the API to update the team
      // For now, just update the local state
      setTeam(prevTeam => prevTeam ? {
        ...prevTeam,
        ...teamFormData
      } : null);
      
      // Update original data and reset changes
      setOriginalTeamData(teamFormData);
      setHasFormChanges(false);
      
      // Reload team data to get updated information
      await loadTeamData();
      
    } catch (error) {
      console.error('Failed to save team:', error);
      alert('Failed to save team. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteTeam = () => {
    setShowDeleteTeamModal(true);
  };

  const confirmDeleteTeam = async () => {
    try {
      setIsDeletingTeam(true);
      setDeleteTeamError(null);
      
      // Team delete logic (existing functionality)
      console.log('Deleting team');
      
      // Close modal first
      setShowDeleteTeamModal(false);
      
      // Navigate back to team information page after successful deletion
      navigate('/my-club/team-information');
    } catch (error: any) {
      console.error('Failed to delete team:', error);
      
      // Show specific error message from API
      let errorMessage = 'Failed to delete team. Please try again.';
      
      if (error.response?.data) {
        const errorData = error.response.data;
        if (errorData.error) {
          errorMessage = errorData.error;
        } else if (errorData.message) {
          errorMessage = errorData.message;
        } else if (errorData.statusMessage) {
          errorMessage = errorData.statusMessage;
        }
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      setDeleteTeamError(errorMessage);
    } finally {
      setIsDeletingTeam(false);
    }
  };

  const closeDeleteTeamModal = () => {
    setShowDeleteTeamModal(false);
    setDeleteTeamError(null);
  };

  const handleTeamLogoUpload = () => {
    teamLogoRef.current?.click();
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>, field: string) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      // Compress the image
      const compressedFile = await imageCompression(file, {
        maxSizeMB: 1,
        maxWidthOrHeight: 800,
        useWebWorker: true
      });

      // Generate file name and context
      const now = new Date();
      const dd = String(now.getDate()).padStart(2, '0');
      const mm = String(now.getMonth() + 1).padStart(2, '0');
      const yyyy = now.getFullYear();
      const ext = compressedFile.type.split('/')[1] || 'jpg';
      const fileName = `team_logo_${Date.now()}.${ext}`;
      const context = `my-club/team-logo/${dd}-${mm}-${yyyy}`;

      // Get presigned URL
      const presignRes = await secureApi.post(
        `/api/v1/general/presigned-url`,
        {
          fileName,
          contentType: compressedFile.type,
          context,
        }
      );

      const presignedUrl = presignRes.data?.presignedUrl;
      const s3Key = presignRes.data?.key;
      
      if (!presignedUrl || !s3Key) {
        throw new Error('Failed to get presigned URL');
      }

      // Upload to S3
      const uploadRes = await fetch(presignedUrl, {
        method: 'PUT',
        headers: { 'Content-Type': compressedFile.type },
        body: compressedFile,
      });
      
      if (!uploadRes.ok) {
        throw new Error('Failed to upload image to S3');
      }

      // Update form data
      setTeamFormData(prev => {
        const newFormData = { ...prev, [field]: s3Key };
        
        // Check if form has changes
        const hasChanges = JSON.stringify(newFormData) !== JSON.stringify(originalTeamData);
        setHasFormChanges(hasChanges);
        
        return newFormData;
      });
      
    } catch (err: any) {
      console.error('File upload failed:', err);
      alert('File upload failed. Please try again.');
    }
  };

  const isTeamFormValid = () => {
    return (
      teamFormData.name.trim() !== '' &&
      teamFormData.shortName.trim() !== '' &&
      teamFormData.ageGroups.length > 0 &&
      teamFormData.coachManagerName.trim() !== '' &&
      teamFormData.contactDetails.trim() !== ''
    );
  };

  const hasApprovedPlayers = () => {
    return players.some(player => player.verificationStatus === 'approved');
  };

  if (loading) {
    return (
      <div className="team-detail-page">
        <Container fluid>
          <div className="mb-3" style={{ padding: '0 15px' }}>
            <Breadcrumbs items={[
              { label: 'My Club' },
              { label: 'Team Information', onClick: () => navigate('/my-club/team-information') },
              { label: 'Team Detail', active: true }
            ]} />
          </div>
          <div className="shimmer-container">
            <div className="shimmer-row" style={{ height: '200px', marginBottom: '20px' }}></div>
            <div className="shimmer-row" style={{ height: '400px' }}></div>
          </div>
        </Container>
      </div>
    );
  }

  if (!team) {
    return (
      <div className="team-detail-page">
        <Container fluid>
          <Alert variant="danger" className="mt-3">
            Team not found.
          </Alert>
        </Container>
      </div>
    );
  }

  return (
    <div className="team-detail-page">
      <Container fluid style={{ padding: '0 15px', marginTop: '20px' }}>
        {/* Breadcrumbs */}
        <div className="mb-3" style={{ padding: '0 15px' }}>
          <Breadcrumbs items={[
            { label: 'My Club' },
            { label: 'Team Information', onClick: () => navigate('/my-club/team-information') },
            { label: team.name, active: true }
          ]} />
        </div>

        {/* Team Information Card */}
        <Card className="mb-4">
          <Card.Header 
            className="card-header-custom"
            onClick={() => setShowTeamFormDetails(!showTeamFormDetails)}
            style={{ cursor: 'pointer' }}
          >
            <div className="d-flex justify-content-between align-items-center">
              <div className="d-flex align-items-center">
                <h4 className="mb-0">Team Information</h4>
                {team?.ageGroups && team.ageGroups.length > 0 && (
                  <div className="d-flex align-items-center ms-2" style={{ gap: '4px' }}>
                    {team.ageGroups.length === 1 ? (
                      <span className="age-category-badge">{team.ageGroups[0]}</span>
                    ) : (
                      team.ageGroups.map((ageGroup, index) => (
                        <span key={index} className="age-category-badge">{ageGroup}</span>
                      ))
                    )}
                  </div>
                )}
              </div>
              <div className="d-flex align-items-center">
                <Button
                  variant="link"
                  className="collapse-btn me-2"
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowTeamFormDetails(!showTeamFormDetails);
                  }}
                >
                  <i className={`ri-arrow-${showTeamFormDetails ? 'up' : 'down'}-s-line`}></i>
                </Button>
              </div>
            </div>
          </Card.Header>
          
          {showTeamFormDetails && (
            <CardBody>

            {isEditMode ? (
              <>
                <Row>
                  <Col md={12}>
                    <Form.Group className="mb-3">
                      <Form.Label>Team Logo<span style={{ color: '#dc3545', fontWeight: 'bold', marginLeft: '2px' }}>*</span></Form.Label>
                      <div className="team-logo-upload-section">
                        <div className="team-logo" onClick={handleTeamLogoUpload}>
                          {teamFormData.logo ? (
                            <img 
                              src={getImageUrl(teamFormData.logo)}
                              alt="Team Logo"
                              onError={(e) => {
                                const target = e.target as HTMLImageElement;
                                target.style.display = 'none';
                              }}
                            />
                          ) : (
                            <div className="logo-placeholder">
                              <div className="upload-icon">
                                <i className="ri-camera-line"></i>
                              </div>
                              <span className="upload-text">Upload Team Logo</span>
                              <span className="upload-hint">Click to browse</span>
                            </div>
                          )}
                        </div>
                        <input
                          type="file"
                          ref={teamLogoRef}
                          accept="image/*"
                          onChange={(e) => handleFileUpload(e, 'logo')}
                          style={{ display: 'none' }}
                        />
                      </div>
                    </Form.Group>
                  </Col>
                </Row>
                
                <Row>
                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label>Team Name<span style={{ color: '#dc3545', fontWeight: 'bold', marginLeft: '2px' }}>*</span></Form.Label>
                      <Form.Control
                        type="text"
                        value={teamFormData.name}
                        onChange={(e) => handleTeamFormChange('name', e.target.value)}
                        placeholder="Enter team name"
                      />
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label>Abbreviation<span style={{ color: '#dc3545', fontWeight: 'bold', marginLeft: '2px' }}>*</span></Form.Label>
                      <Form.Control
                        type="text"
                        value={teamFormData.shortName}
                        onChange={(e) => handleTeamFormChange('shortName', e.target.value)}
                                                  placeholder="Enter abbreviation"
                      />
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label>Age Categories<span style={{ color: '#dc3545', fontWeight: 'bold', marginLeft: '2px' }}>*</span></Form.Label>
                      <Select
                        isMulti={false}
                        value={teamFormData.ageGroups && teamFormData.ageGroups.length > 0 
                          ? { value: teamFormData.ageGroups[0], label: teamFormData.ageGroups[0] }
                          : null
                        }
                        onChange={(selectedOption) => {
                          const selectedValue = selectedOption ? selectedOption.value : '';
                          setTeamFormData(prev => ({ ...prev, ageGroups: selectedValue ? [selectedValue] : [] }));
                        }}
                        options={ageCategories.map(cat => ({ value: cat, label: cat }))}
                        placeholder="Select age category"
                        className="react-select"
                        classNamePrefix="react-select"
                        isDisabled={team.players > 0}
                        maxMenuHeight={400}
                        menuPosition="fixed"
                        menuPortalTarget={document.body}
                        styles={{
                          menu: (base) => ({ 
                            ...base, 
                            zIndex: 99999,
                            maxHeight: 400,
                            overflow: 'hidden'
                          }),
                          menuPortal: (base) => ({ 
                            ...base, 
                            zIndex: 99999 
                          }),
                          menuList: (base) => ({
                            ...base,
                            maxHeight: 400,
                            padding: 0,
                            overflowY: 'auto',
                            overflowX: 'hidden'
                          })
                        }}
                      />
                      {team.players > 0 && (
                        <div className="form-text text-muted mt-1">
                          <i className="ri-information-line me-1"></i>
                          Age category cannot be changed once players are registered to the team
                        </div>
                      )}
                    </Form.Group>
                  </Col>
                </Row>
                
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                                              <Form.Label>Coach/Manager Name</Form.Label>
                      <Form.Control
                        type="text"
                        value={teamFormData.coachManagerName}
                        onChange={(e) => handleTeamFormChange('coachManagerName', e.target.value)}
                        placeholder="Enter coach/manager name"
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Contact Details<span style={{ color: '#dc3545', fontWeight: 'bold', marginLeft: '2px' }}>*</span></Form.Label>
                      <Form.Control
                        type="text"
                        value={teamFormData.contactDetails}
                        onChange={(e) => handleTeamFormChange('contactDetails', e.target.value)}
                        placeholder="Enter contact number"
                      />
                    </Form.Group>
                  </Col>
                </Row>
                
                {/* Action Buttons - Placed below form fields for better UX */}
                <div className="d-flex justify-content-end mt-4">
                  {!hasApprovedPlayers() && (
                    <Button 
                      variant="outline-danger" 
                      className="me-2" 
                      onClick={handleDeleteTeam}
                    >
                      Delete Team
                    </Button>
                  )}
                  {hasFormChanges && (
                    <Button 
                      variant="primary" 
                      onClick={handleSaveTeam}
                      disabled={!isTeamFormValid() || isSubmitting}
                    >
                      {isSubmitting ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                          Updating...
                        </>
                      ) : (
                        'Update Team'
                      )}
                    </Button>
                  )}
                </div>

              </>
            ) : (
              <Row>
                <Col md={2}>
                  <div className="team-logo-container" style={{ 
                    width: '80px', 
                    height: '80px', 
                    borderRadius: '8px', 
                    overflow: 'hidden',
                    border: '2px solid #e0e0e0',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: '#f8f9fa'
                  }}>
                    {team.logo && team.logo !== '' && !team.logo.includes('association') && !team.logo.includes('banner') ? (
                      <img 
                        src={getImageUrl(team.logo)}
                        alt="Team Logo"
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover',
                          maxWidth: '100%',
                          maxHeight: '100%'
                        }}
                        onError={(e) => {
                          const target = e.target as HTMLImageElement;
                          target.style.display = 'none';
                          target.nextElementSibling?.classList.remove('d-none');
                        }}
                        onLoad={(e) => {
                          const target = e.target as HTMLImageElement;
                          // Check if image is too large and replace with default
                          if (target.naturalWidth > 200 || target.naturalHeight > 200) {
                            console.log('Image too large, using default logo');
                            target.style.display = 'none';
                            target.nextElementSibling?.classList.remove('d-none');
                          }
                        }}
                      />
                    ) : null}
                    {(!team.logo || team.logo === '' || team.logo.includes('association') || team.logo.includes('banner')) && (
                      <div className="default-team-logo-detail">
                        <i className="ri-team-line"></i>
                      </div>
                    )}
                  </div>
                </Col>
                <Col md={10}>
                  <div className="team-info-details">
                    <Row>
                      <Col md={6}>
                        <div className="info-item mb-2">
                          <strong>Team Name:</strong> <span className="text-primary">{team.name}</span>
                        </div>
                        <div className="info-item mb-2">
                          <strong>Abbreviation:</strong> <span className="text-secondary">{team.shortName}</span>
                        </div>
                        <div className="info-item mb-2">
                          <strong>Coach/Manager:</strong> <span>{team.coachManagerName}</span>
                        </div>
                      </Col>
                      <Col md={6}>
                        <div className="info-item mb-2">
                          <strong>Contact:</strong> <span>{team.contactDetails}</span>
                        </div>
                        <div className="info-item mb-2">
                          <strong>Age Categories:</strong> 
                          <span className="badge bg-info ms-2">
                            {team.ageGroups.join(', ')}
                          </span>
                        </div>
                        <div className="info-item mb-2">
                          <strong>Total Players:</strong> 
                          <span className="badge bg-success ms-2">{team.players}</span>
                        </div>
                      </Col>
                    </Row>
                  </div>
                </Col>
              </Row>
            )}
          </CardBody>
          )}
        </Card>

        {/* Add/Edit Player Form - Show inline when adding/editing */}
        {showPlayerForm && (
          <Card className="mb-4">
            <CardBody>
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h5 className="mb-0">{editingPlayer ? 'Edit Player Details' : 'Add Player Details'}</h5>
                <Button 
                  variant="link" 
                  className="close-form-btn"
                  onClick={handleCancelAddPlayer}
                >
                  <i className="ri-close-line"></i>
                </Button>
              </div>

              <PlayerForm
                player={editingPlayer}
                teamId={teamId || ''}
                teamName={team.name}
                ageCategories={ageCategories}
                showTeamField={false} // Hide team field in Team Information
                teamAgeCategories={team?.ageGroups} // Pass team's age categories array
                hideAgeCategoryField={!editingPlayer} // Hide age category field when adding new player
                // No tournamentId prop - will use dynamic getCurrentActiveTournament()
                onSubmit={handleSavePlayer}
                onCancel={handleCancelAddPlayer}
                isSubmitting={isSubmitting}
                submitError={submitError}
                isReadOnly={editingPlayer?.verificationStatus === 'approved' || location.state?.mode === 'view'}
              />
            </CardBody>
          </Card>
        )}

        {/* Player Assets - Show when viewing/editing a player */}
        {PLAYER_ASSETS_ENABLED && showPlayerForm && editingPlayer && (
          <Card className="mb-4">
            <CardBody>
              <h5 className="card-title mb-4">Player Assets</h5>
              <Row className="g-3">
                <Col md={6}>
                  <div className="asset-card">
                    <div className="d-flex justify-content-between align-items-start mb-3">
                      <div>
                        <h6 className="mb-1">QR Code</h6>
                        <p className="text-muted mb-0">Scan to verify player details instantly.</p>
                      </div>
                    </div>
                    <div className="d-flex gap-2 flex-wrap">
                      <Button
                        variant="outline-dark"
                        onClick={() => {
                          const assetUrl = editingPlayer.qrCode ? getImageUrl(editingPlayer.qrCode) : '';
                          if (assetUrl) {
                            handleAssetView({
                              _id: editingPlayer._id,
                              fullName: editingPlayer.name,
                              qrCodeUrl: assetUrl,
                              playerIdCardUrl: editingPlayer.playerIdCard ? getImageUrl(editingPlayer.playerIdCard) : ''
                            }, 'qrCode');
                          }
                        }}
                        disabled={!editingPlayer.qrCode}
                      >
                        View
                      </Button>
                      <Button
                        variant="success"
                        onClick={() => {
                          const assetUrl = editingPlayer.qrCode ? getImageUrl(editingPlayer.qrCode) : '';
                          if (assetUrl) {
                            handleAssetDownload({
                              _id: editingPlayer._id,
                              fullName: editingPlayer.name,
                              qrCodeUrl: assetUrl,
                              playerIdCardUrl: editingPlayer.playerIdCard ? getImageUrl(editingPlayer.playerIdCard) : ''
                            }, 'qrCode');
                          }
                        }}
                        disabled={!editingPlayer.qrCode}
                      >
                        Download
                      </Button>
                    </div>
                  </div>
                </Col>
                <Col md={6}>
                  <div className="asset-card">
                    <div className="d-flex justify-content-between align-items-start mb-3">
                      <div>
                        <h6 className="mb-1">Player ID Card</h6>
                        <p className="text-muted mb-0">Share or print this ID for match-day checks.</p>
                      </div>
                    </div>
                    <div className="d-flex gap-2 flex-wrap">
                      <Button
                        variant="outline-dark"
                        onClick={() => {
                          const assetUrl = editingPlayer.playerIdCard ? getImageUrl(editingPlayer.playerIdCard) : '';
                          if (assetUrl) {
                            handleAssetView({
                              _id: editingPlayer._id,
                              fullName: editingPlayer.name,
                              qrCodeUrl: editingPlayer.qrCode ? getImageUrl(editingPlayer.qrCode) : '',
                              playerIdCardUrl: assetUrl
                            }, 'playerIdCard');
                          }
                        }}
                        disabled={!editingPlayer.playerIdCard}
                      >
                        View
                      </Button>
                      <Button
                        variant="success"
                        onClick={() => {
                          const assetUrl = editingPlayer.playerIdCard ? getImageUrl(editingPlayer.playerIdCard) : '';
                          if (assetUrl) {
                            handleAssetDownload({
                              _id: editingPlayer._id,
                              fullName: editingPlayer.name,
                              qrCodeUrl: editingPlayer.qrCode ? getImageUrl(editingPlayer.qrCode) : '',
                              playerIdCardUrl: assetUrl
                            }, 'playerIdCard');
                          }
                        }}
                        disabled={!editingPlayer.playerIdCard}
                      >
                        Download
                      </Button>
                    </div>
                  </div>
                </Col>
              </Row>
            </CardBody>
          </Card>
        )}

        {/* Players Section */}
        {team && (
          <Card>
            <CardBody>
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h4 className="mb-0">Players</h4>
                <AddNewButton onClick={handleAddPlayer} label="Add Player" />
              </div>

              <PlayerTable
              players={players.map(player => {
                return {
                  _id: player._id,
                  playerId: player.playerId,
                  fullName: player.name,
                  team: {
                    name: team?.name || 'Unknown Team',
                    shortName: team?.shortName || 'UT',
                    logo: team?.logo || ''
                  },
                  dob: player.dob,
                  sports: 'Football',
                  verifyStat: player.verificationStatus === 'approved' ? 'Approved' : 
                             player.verificationStatus === 'referred_back' ? 'Referred Back' : 'Pending',
                  paymentStat: 'Not Paid', // Default value since payment columns are hidden
                  amountPaid: 0, // Default value since payment columns are hidden
                  amountDue: 0, // Default value since payment columns are hidden
                  playerImage: player.profilePic && player.profilePic !== '' ? 
                    (player.profilePic.startsWith('http') ? player.profilePic : `${getImageUrl(player.profilePic)}`) : 
                    '',
                  ageCategory: player.ageCategory,
                  qrCodeUrl: player.qrCode ? getImageUrl(player.qrCode) : '',
                  playerIdCardUrl: player.playerIdCard ? getImageUrl(player.playerIdCard) : ''
                };
              })}
              loading={loading}
              showView={true}
              showEdit={true}
              showDelete={true}
              showPaymentColumns={false}
              showQrActions={PLAYER_ASSETS_ENABLED}
              showPlayerIdCardActions={PLAYER_ASSETS_ENABLED}
              assetColumnMode="separate"
              onAssetView={handleAssetView}
              onAssetDownload={handleAssetDownload}
              onView={(player) => handleViewPlayer({
                _id: player._id,
                name: player.fullName,
                profilePic: player.playerImage,
                dob: player.dob,
                ageCategory: player.ageCategory,
                verificationStatus: player.verifyStat === 'Approved' ? 'approved' : 
                                   player.verifyStat === 'Referred Back' ? 'referred_back' : 'pending',
                createdAt: new Date().toISOString()
              })}
              onEdit={(player) => {
                handleEditPlayer({
                  _id: player._id,
                  name: player.fullName,
                  profilePic: player.playerImage,
                  dob: player.dob,
                  ageCategory: player.ageCategory,
                  verificationStatus: player.verifyStat === 'Approved' ? 'approved' : 
                                     player.verifyStat === 'Referred Back' ? 'referred_back' : 'pending',
                  createdAt: new Date().toISOString()
                });
              }}
              onDelete={(player) => handleDeletePlayer(player._id)}
              title="Player Lineups"
              emptyMessage="No players found"
              emptySubMessage="Add players to this team to get started."
            />
          </CardBody>
        </Card>
        )}


      </Container>

      {/* Delete Team Modal */}
      <DeleteModal
        show={showDeleteTeamModal}
        handleClose={closeDeleteTeamModal}
        deleteModalFunction={confirmDeleteTeam}
        title="Delete Team"
        message={deleteTeamError || `Are you sure you want to delete "${team?.name}"? This action cannot be undone.`}
        confirmLabel={isDeletingTeam ? "Deleting..." : "Yes, Delete"}
        cancelLabel="Cancel"
        isLoading={isDeletingTeam}
        error={deleteTeamError}
      />

      {/* Delete Player Modal */}
      <DeleteModal
        show={showDeletePlayerModal}
        handleClose={closeDeletePlayerModal}
        deleteModalFunction={confirmDeletePlayer}
        title="Delete Player"
        message={deletePlayerError || `Are you sure you want to delete "${deletePlayerTarget?.name}"? This action cannot be undone.`}
        confirmLabel={isDeletingPlayer ? "Deleting..." : "Yes, Delete"}
        cancelLabel="Cancel"
        isLoading={isDeletingPlayer}
        error={deletePlayerError}
      />
      {PLAYER_ASSETS_ENABLED && (
        <PlayerAssetPreviewModal
          show={assetPreviewConfig !== null}
          assetType={assetPreviewConfig?.type || 'qrCode'}
          assetUrl={
            assetPreviewConfig
              ? assetPreviewConfig.type === 'qrCode'
                ? assetPreviewConfig.player.qrCodeUrl
                : assetPreviewConfig.player.playerIdCardUrl
              : undefined
          }
          playerName={assetPreviewConfig?.player.fullName}
          onClose={() => setAssetPreviewConfig(null)}
          onDownload={() => {
            if (!assetPreviewConfig) return;
            handleAssetDownload(assetPreviewConfig.player, assetPreviewConfig.type);
          }}
        />
      )}
    </div>
  );
};

export default TeamDetail; 